import { Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString, TextareaField } from "common";
import { produce } from "immer";
import React from "react";
import { EquipmentCatalogItem } from "types";
import { object, string } from "yup";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

interface EditEquipmentValues {
    name: string;
    displayName: string;
    caption: string;
}

const validationSchema = object({
    name: requiredString(),
    displayName: string(),
    caption: string(),
});

const DISPLAY_NAME_TOOLTIP = "This name will be displayed to customers at checkout and across all communications.";

interface EditEquipmentModalProps {
    isOpen: boolean;
    catalogItem: EquipmentCatalogItem;
    onSubmit: (catalogItem: EquipmentCatalogItem) => void;
    onClose: React.MouseEventHandler;
}

export const EditEquipmentModal = ({ isOpen, catalogItem, onSubmit, onClose }: EditEquipmentModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent catalogItem={catalogItem} onSubmit={onSubmit} onClose={onClose} />
        </Modal>
    );
};

type ModalContentProps = Omit<EditEquipmentModalProps, "isOpen">;

const ModalContent = ({ catalogItem, onSubmit, onClose }: ModalContentProps) => {
    const initialValues: EditEquipmentValues = {
        name: catalogItem?.name ?? "",
        displayName: catalogItem?.displayName ?? "",
        caption: catalogItem?.caption ?? "",
    };

    const handleSubmit = async (values: EditEquipmentValues) => {
        const updatedCatalogItem = produce(catalogItem, (draft) => {
            draft.displayName = values.displayName || catalogItem?.name;
            draft.caption = values.caption;
            draft.overrideParent = true;
        });

        onSubmit(updatedCatalogItem);
    };

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, dirty }) => (
                <>
                    <Modal.Header>Edit Equipment</Modal.Header>

                    <Modal.Body>
                        <fieldset className="space-y-6">
                            <FormField name="name" label="Name">
                                <InputField disabled isRequired placeholder="Name" />
                            </FormField>

                            <FormField name="displayName" label="Display Name" tooltip={DISPLAY_NAME_TOOLTIP}>
                                <InputField placeholder="Display Name" />
                            </FormField>

                            <FormField name="caption" label="Caption">
                                <TextareaField rows={5} placeholder="Caption" />
                            </FormField>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton disabled={!dirty} onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
